import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

/**
 * BranchSalesSummary
 *
 * Props:
 *   data: Array<{
 *     Branch_ID: string,
 *     Branch_Name: string,
 *     CashSale: number,
 *     CreditSale: number,
 *     CashReturn: number,
 *     CreditReturn: number,
 *     NetAmount: number,
 *   }>
 *
 * Example:
 *   <BranchSalesSummary data={[
 *     {
 *       Branch_ID: "MAIN      ",
 *       Branch_Name: "HO - STORE",
 *       CashSale: 2636.4,
 *       CreditSale: 161237.95,
 *       CashReturn: -25.0,
 *       CreditReturn: 0.0,
 *       NetAmount: 163849.35,
 *     },
 *   ]} />
 */
const CHART_COLORS = {
  CashSale: "#6366f1",
  CreditSale: "#10b981",
  CashReturn: "#ef4444",
  CreditReturn: "#f59e0b",
  NetAmount: "#0f172a",
};

const fmtMoney = (n) =>
  Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const DailySales = ({ data = [] , onClose}) => {
  const rows = (data || []).map((d) => ({
    ...d,
    Branch_ID: (d.Branch_ID || "").trim(),
    Branch_Name: (d.Branch_Name || "").trim(),
  }));

  const chartData = rows.map((d) => ({
    name: d.Branch_Name || d.Branch_ID,
    CashSale: d.CashSale,
    CreditSale: d.CreditSale,
    CashReturn: d.CashReturn,
    CreditReturn: d.CreditReturn,
    NetAmount: d.NetAmount,
  }));

  if (!rows.length) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: "#94a3b8",
          fontSize: 13,
          border: "1px solid #e2e8f0",
          borderRadius: 12,
        }}
      >
        No branch sales data available.
      </div>
    );
  }

  // Summary totals across all branches passed in
  const totals = rows.reduce(
    (acc, d) => ({
      CashSale: acc.CashSale + (d.CashSale || 0),
      CreditSale: acc.CreditSale + (d.CreditSale || 0),
      CashReturn: acc.CashReturn + (d.CashReturn || 0),
      CreditReturn: acc.CreditReturn + (d.CreditReturn || 0),
      NetAmount: acc.NetAmount + (d.NetAmount || 0),
    }),
    { CashSale: 0, CreditSale: 0, CashReturn: 0, CreditReturn: 0, NetAmount: 0 }
  );

  const summaryItems = [
    { label: "Cash Sale", value: totals.CashSale, color: CHART_COLORS.CashSale },
    { label: "Credit Sale", value: totals.CreditSale, color: CHART_COLORS.CreditSale },
    { label: "Cash Return", value: totals.CashReturn, color: CHART_COLORS.CashReturn },
    { label: "Credit Return", value: totals.CreditReturn, color: CHART_COLORS.CreditReturn },
    { label: "Net Amount", value: totals.NetAmount, color: CHART_COLORS.NetAmount },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 20,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* ── Branch label ────────────────────────────────────────── */}
      <div>
        <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>
          {rows.length === 1 ? "Branch" : `${rows.length} Branches`}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
          {rows.length === 1 ? rows[0].Branch_Name : "All Branches"}
        </div>
      </div>

      {/* ── Summary text + labels ──────────────────────────────── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        {summaryItems.map((item) => (
          <div key={item.label} style={{ minWidth: 130 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                color: "#64748b",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: item.color,
                  flexShrink: 0,
                }}
              />
              {item.label}
            </div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                color:
                  item.value < 0 && (item.label.includes("Return"))
                    ? "#ef4444"
                    : "#0f172a",
              }}
            >
              {fmtMoney(item.value)}
            </div>
          </div>
        ))}
      </div>

      {/* ── Chart ───────────────────────────────────────────────── */}
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          background: "#fff",
          padding: "16px 12px 8px",
        }}
      >
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 4, right: 12, left: 0, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                }
              />
              <Tooltip
                formatter={(value) => fmtMoney(value)}
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="CashSale"
                name="Cash Sale"
                fill={CHART_COLORS.CashSale}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="CreditSale"
                name="Credit Sale"
                fill={CHART_COLORS.CreditSale}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="CashReturn"
                name="Cash Return"
                fill={CHART_COLORS.CashReturn}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="CreditReturn"
                name="Credit Return"
                fill={CHART_COLORS.CreditReturn}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="NetAmount"
                name="Net Amount"
                fill={CHART_COLORS.NetAmount}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

        {/* Sticky Close Bar */}
        <div
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "flex-end",
          padding: "8px 4px",
          marginTop: "12px",
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <button
          onClick={onClose}
          className="w-full py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:opacity-95 active:scale-[0.99] transition-all tracking-wider uppercase"
        >
          Close
        </button>
      </div>

    </div>
  );
};

export default DailySales;